import { removeUidFromObject } from "./sb-fixer";
import { ISBEmbeddedSvods } from "../types/sb";
import { ComponentNames } from "../constant";
import { logError } from "./logger";

/**
 * Join SVOD display names into a human-readable list using the same
 * punctuation style as the channel-lineup page title:
 *   - 0 items → ""
 *   - 1 item  → "A"
 *   - 2 items → "A and B"
 *   - 3+      → "A, B and C" (no Oxford comma)
 * Pure — does not mutate the input array.
 */
export const formatSvodList = (arr: readonly string[] | undefined): string => {
  if (!arr || arr.length === 0) {
    return "";
  }
  if (arr.length === 1) {
    return arr[0];
  }
  if (arr.length === 2) {
    return `${arr[0]} and ${arr[1]}`;
  }
  const head = arr.slice(0, -1);
  const last = arr[arr.length - 1];
  return `${head.join(", ")} and ${last}`;
};

/**
 * WAT-11710: collapse SVOD entries that are the SAME product reached two different ways (e.g.
 * "Disney+ Basic" via MyKids and the Disney+ half of the Disney+/Hulu Duo bundle) into their FIRST
 * occurrence, preserving input order. Content owners mark such entries with a shared, non-empty
 * `productGroup` value.
 *
 * The grouping key is `productGroup` ONLY — never the logo/icon URL and never the resolved name.
 * HBO Max Standard and HBO Max Basic With Ads share a logo but are genuinely different products and
 * must stay two entries; the resolved name comes from EPOCH's svodNameMapping, which can change
 * without a code release.
 *
 * An empty/absent/whitespace-only productGroup always keeps its own entry (today's behavior,
 * unchanged). Pure — does not mutate the input array or its elements.
 */
type SameProductSvod = { productGroup?: string; packageListSystem?: string[]; genrePacks?: string[]; interactiveToolData?: any };

// WAT-11710: union `field` from `svod` onto the survivor `kept`. Only write the key if the union has
// content or `kept` already carried it, so an entry that never carried these fields keeps its exact
// shape (the "nothing marked in the CMS changes" guarantee covers shape too).
const unionRouteField = (kept: SameProductSvod, svod: SameProductSvod, field: "packageListSystem" | "genrePacks") => {
  const merged = [...new Set([...(kept?.[field] || []), ...(svod?.[field] || [])])];
  if (merged.length || field in kept) {
    kept[field] = merged;
  }
};

export const collapseSameProductSvods = <T extends SameProductSvod>(svods: readonly T[] | undefined): T[] => {
  const keptByGroup = new Map<string, T>();
  return (svods || []).reduce((collapsed: T[], svod: T) => {
    // Case-folded: productGroup is hand-authored free text in the CMS, so "Disney+" vs "disney+"
    // must group. Must stay identical to mergeSameProductRows in getSVODS.ts.
    const group = `${svod?.productGroup || ""}`.trim().toLowerCase();
    if (!group) {
      collapsed.push(svod);
      return collapsed;
    }
    const kept = keptByGroup.get(group);
    if (!kept) {
      // Copy so the union below never mutates the caller's object.
      const firstOfGroup = { ...svod };
      keptByGroup.set(group, firstOfGroup);
      collapsed.push(firstOfGroup);
      return collapsed;
    }
    // WAT-11710: the two entries exist BECAUSE their package routes differ (one via a genre pack,
    // one via a signature bundle). Union the routes onto the survivor — dropping them would make the
    // tool claim the subscription is not in a package that includes it, and an entry left with no
    // route at all is filtered out of the tile list entirely.
    unionRouteField(kept, svod, "packageListSystem");
    // NOTE: genrePacks order is deliberately NOT canonicalized. packageListSystem has a canonical
    // tier order (orderPackageListSystem) that the union would break, so the adapter re-applies it;
    // genre ids have no canonical order anywhere in this codebase, so there is nothing to restore.
    unionRouteField(kept, svod, "genrePacks");
    // WAT-11710: the modal's signature legal copy is interactiveToolData.deviceIncludedText and the
    // tile logo is interactiveToolData.icon — the adapter reads both, behind && / || guards.
    //
    // 🔴 A whole-object guard here is UNREACHABLE (PR Council, 2026-08-21): by the time the collapse
    // runs, EVERY entry already has the object. enabledSvodFilterInteractiveTool keeps only entries
    // whose interactiveToolData.disable === false (an absent object fails that test), and
    // embeddedSVODParserInteractiveTool destructures it with a `= {}` default. So
    // `if (!kept.interactiveToolData)` never fired and the later half's legal copy was still lost.
    //
    // Merge FIELD BY FIELD: fill only what the survivor left empty, never overwrite what it has.
    // Copy the nested object first — `kept` is a shallow spread, so its interactiveToolData still
    // points at the caller's object and writing through it would break the purity contract.
    if (svod?.interactiveToolData) {
      const merged = { ...(kept?.interactiveToolData || {}) };
      if (!merged?.deviceIncludedText && svod?.interactiveToolData?.deviceIncludedText) {
        merged.deviceIncludedText = svod?.interactiveToolData?.deviceIncludedText;
      }
      if (!merged?.icon && svod?.interactiveToolData?.icon) {
        merged.icon = svod?.interactiveToolData?.icon;
      }
      kept.interactiveToolData = merged;
    }
    return collapsed;
  }, []);
};

export const addPackageDetailsToEmbeddedSvods = (args: {
  embeddedSvods: ISBEmbeddedSvods[];
  epochSvodsData: any;
  packageConfig: any;
  componentType: any;
  signaturePackages?: any;
  svodMapping?: any;
  // WAT-11085: EPOCH genreSvodOffers.svodNameMapping so name-rendering surfaces (in-page chlup)
  // resolve the display name from EPOCH instead of the embedded CMS name.
  svodNameMapping?: Record<string, string>;
  isSatellite?: boolean;
  excludedSvods?: any[];
  pkgId?: string;
  pkgType?: string;
  singlePackageView?: boolean;
  singlePackageConfig?: any;
}) => {
  const { epochSvodsData, packageConfig, componentType, signaturePackages, svodMapping, svodNameMapping, isSatellite, pkgId, pkgType, singlePackageView, singlePackageConfig, excludedSvods } = args;
  let { embeddedSvods } = args;

  const pkgMatchText = isSatellite ? "DTV_TV" : "ATT_TV";
  // Adding packageListSystem for Embedded & Included SVODs retrieved from epoch getOffers
  let filteredPackages =
    Array.isArray(packageConfig || []) && packageConfig?.length > 0 ?
      packageConfig?.filter(
        (pkg: any) => pkg?.id?.startsWith?.(pkgMatchText) &&
            epochSvodsData &&
            Object.keys(epochSvodsData || {}).includes(pkg?.queryparam)
      ) :
      [];
  filteredPackages = filteredPackages?.reduce?.((acc, curr) => {
    if (!curr?.queryparam) {
      return acc;
    }
    acc[curr?.queryparam] = curr?.id;
    return acc;
  }, {});
  embeddedSvods = embeddedSvods && removeUidFromObject?.(embeddedSvods || []) || [];
  // [WAT-11772] One code -> EPOCH-name map, built once, shared with every other surface.
  const epochNameByCode = buildIncludedSvodNameMap(epochSvodsData as any);
  embeddedSvods = embeddedSvods?.length > 0 && embeddedSvods?.map?.((item: ISBEmbeddedSvods) => {
    const packageListSystem = [];
    const offerCode = isSatellite ? item?.offerCodeSatellite : item?.offerCode;
    epochSvodsData && Object.keys(epochSvodsData || {})?.forEach?.((key: string) => {
      epochSvodsData && epochSvodsData?.[key]?.forEach?.((svod: any) => {
        if (svod?.code === offerCode && filteredPackages?.[key]) {
          packageListSystem?.push?.(filteredPackages?.[key]);
        }
      });
    });
    // [WAT-11772] Resolve the EPOCH name through the SHARED map instead of a local
    // last-one-wins scan. This used to reassign `epochPackageName` on every matching
    // package, so it kept the LAST name EPOCH returned for the code, while
    // buildIncludedSvodNameMap — the resolver every other svod surface uses, including the
    // genre-pack Subscriptions row — keeps the FIRST (`!map[svod.code]`). One code that
    // EPOCH names differently under two package queryparams therefore resolved to two
    // different strings depending on which surface asked, and a name-equality match
    // between those surfaces silently failed. One rule now, in one place.
    const epochPackageName = offerCode ? epochNameByCode?.[offerCode] : "";
    return {
      ...item,
      packageListSystem: packageListSystem ? packageListSystem : [],
      name: epochPackageName ? epochPackageName : item?.name ? item?.name : ""
    };
  }) || [];

  // When given svodMapping in params adding genrePacks each svod belongs to
  if( svodMapping ) {
    embeddedSvods?.forEach(svod => {
      const genrePacks = []
      const offerCode = isSatellite ? svod?.offerCodeSatellite : svod?.offerCode;
      svodMapping && Object.keys(svodMapping || {})?.forEach(genre => {
        svodMapping?.[genre]?.forEach(id => {
          if(id === offerCode) {
            genrePacks?.push(genre);
          }
        })
      })
      svod.genrePacks = genrePacks;
    })
  }

  // Component Specific Filtering and Parsing
  // This is to ensure that we only send the data required by the component
  switch (componentType) {
    case ComponentNames?.CHANNEL_LISTING_PAGE:
    case ComponentNames?.SATELLITE_CHANNEL_LISTING_PAGE :{
      // filterOut only SVODs that are enabled through CMS
      embeddedSvods = enabledSvodFilterChlup(embeddedSvods);
      // Embedded & Included Parser to send only Data Required by component
      const parsedComponentData = embeddedSVODParserChlup(embeddedSvods, signaturePackages, isSatellite);
      return parsedComponentData || [];
    }
    case ComponentNames?.PLANCARD: {
      // filterOut only SVODs that are enabled through CMS
      embeddedSvods = enabledSvodFilterPlanCards(embeddedSvods, excludedSvods, isSatellite);
      // Embedded & Included Parser to send only Data Required by component
      const parsedComponentData = embeddedSVODParserPlanCards(embeddedSvods);
      return parsedComponentData || [];
    }
    case ComponentNames?.INPAGECHANNELPACKLINEUP: {
      return enabledSvodFilterInPageChlup(embeddedSvods, pkgId, pkgType, svodNameMapping);
    }
    case ComponentNames?.CHLUP_INTERACTIVE_TOOL: {
      // filterOut only SVODs that are enabled through CMS
      embeddedSvods = enabledSvodFilterInteractiveTool(embeddedSvods, isSatellite);
      embeddedSvods = singlePackageFilterInteractiveTool({
        embeddedSvods,
        singlePackageView,
        singlePackageConfig
      });
      // Embedded & Included Parser to send only Data Required by component
      const parsedComponentData = embeddedSVODParserInteractiveTool(embeddedSvods, svodNameMapping);
      return parsedComponentData || [];
    }
    default: return embeddedSvods || [];
  }
};

export const enabledSvodFilterInPageChlup = (
  embeddedSvods: ISBEmbeddedSvods[] = [],
  pkgId?: string,
  pkgType?: string,
  svodNameMapping?: SvodNameMapping
) => embeddedSvods
  .filter(svod => {
    // Only include SVODs that are not disabled
    const item = svod.inPageChlupData;
    return item && item.disable !== true &&
        (!pkgId || !pkgType || ((pkgType === "genre" ? svod.genrePacks : svod.packageListSystem) ?? []).includes(pkgId));
  })
  .map(svod => {
    const item = svod.inPageChlupData;
    return {
      ...item,
      icon: item.icon,
      // WAT-11085: logo-bearing surface -> by-entry resolver (displayName -> EPOCH svodNameMapping ->
      // embedded name). For DTVI/DTVS svods (absent from svodNameMapping), svod.name is already the
      // EPOCH offer name set by addPackageDetailsToEmbeddedSvods, so it is preserved.
      name: resolveGenreSvodNameByEntry(svod, {
        svodNameMapping,
        code: svod?.offerCode
      })
    };
  });

export const enabledSvodFilterChlup = (embeddedSvods: ISBEmbeddedSvods[]) => (
  embeddedSvods?.filter?.((svod: ISBEmbeddedSvods) => svod?.chlupData?.disable === false)?.filter?.(svod => svod?.packageListSystem?.length > 0) || []
);

export const embeddedSVODParserChlup = (embeddedSvods: ISBEmbeddedSvods[], signaturePackages, isSatellite) => {
  const packages = signaturePackages?.filter?.(pkg => pkg?.category === "ENGLISH" || pkg?.categoryId === "ENGLISH") || [];
  // Parse the embedded SVODs to extract relevant data
  return embeddedSvods?.map?.((svod: ISBEmbeddedSvods) => {
    const { name, chlupData } = svod;
    return {
      name: svod.displayName || name,
      // [WAT-11772] Keep the offer code on the parsed shape. It is the only STABLE identity
      // a subscription has: the rendered label differs between this list and the genre-pack
      // Subscriptions row for the same product ("Disney+" vs "Disney+ Basic", "HBO Max
      // Standard" vs "HBO Max Basic With Ads" on the live feed), so matching a search
      // selection to a pack row by label cannot be made reliable. Dropping the code here is
      // why it had to be.
      offerCode: svod?.offerCode,
      icon: chlupData?.icon,
      svodDeviceIncludedText: chlupData?.deviceIncludedText,
      indicators: Array.from({ length: packages?.length || 0 })?.map?.((_, ix) => {
        const satPkgId = packages?.[ix]?.packageId || packages?.[ix]?.id
        const transformedPackageId = isSatellite ? satPkgId?.replace(/^(ATT(_TV)?|DTV(_TV)?)_(.*)$/, 'DTV_TV_$4') : packages?.[ix]?.id ;
        return svod?.packageListSystem?.includes?.(transformedPackageId) ? 1 : null;
      }),
      chnlPackages: svod?.packageListSystem || []
    };
  }) || [];
}

export const enabledSvodFilterPlanCards = (embeddedSvods: ISBEmbeddedSvods[], excludedSvods: any, isSatellite: boolean) => {
  const excludedSvodsMap = excludedSvods?.reduce((state: any, iterator: any) => {
    state[iterator?.id] = iterator?.excludedPackages || [];
    return state;
  }, {});
  // Filter out the SVODs that are not enabled
  return embeddedSvods?.map?.((svod: ISBEmbeddedSvods) => {
    const offerCode = isSatellite ? svod?.offerCodeSatellite : svod?.offerCode;
    // Filter out excluded packages using global config
    let filteredPackageListSystem = svod?.packageListSystem?.filter((pkg: any) => !svod?.planCardsData?.excludedPackages?.includes(pkg)) || [];
    // Filter out excluded packages on page level
    filteredPackageListSystem = filteredPackageListSystem?.filter((pkg: any) => !excludedSvodsMap?.[offerCode]?.includes(pkg)) || [];
    return {
      ...svod,
      packageListSystem: filteredPackageListSystem
    };
  })?.filter((svod: any) => svod?.packageListSystem?.length > 0);
}

export const embeddedSVODParserPlanCards = (embeddedSvods: ISBEmbeddedSvods[]) => (
  embeddedSvods?.map?.((svod: ISBEmbeddedSvods) => {
    const { name, planCardsData } = svod;
    return {
      name: svod.displayName || name,
      planCardsData,
      packageListSystem: svod?.packageListSystem
    };
  }) || []
);

// WAT-11710: tier order IS render order downstream — buildSvodPackageLists pushes into
// signaturePackageList in array order. Extracted from enabledSvodFilterInteractiveTool (its only
// caller until now) so any code that REBUILDS the list can re-canonicalize it; collapseSameProductSvods
// unions two entries ids and therefore appends at the tail. Unknown ids keep their relative order
// at the tail, exactly as the original loop did.
export const orderPackageListSystem = (packageListSystem?: readonly string[], isSatellite?: boolean): string[] => {
  const tierOrder = isSatellite ? ["DTV_TV_ENTERTAINMENT", "DTV_TV_CHOICE", "DTV_TV_ULTIMATE", "DTV_TV_PREMIER"] : ["ATT_TV_ENTERTAINMENT", "ATT_TV_CHOICE", "ATT_TV_ULTIMATE", "ATT_TV_PREMIER"];
  const tierRank = (pkg: string) => {
    const ix = tierOrder.indexOf(pkg);
    return ix === -1 ? tierOrder.length : ix;
  };
  // Array.prototype.sort is spec-guaranteed stable (ES2019+): ids outside tierOrder share the same
  // rank and keep their original relative order at the tail, exactly like the previous ordered/rest split.
  return [...(packageListSystem || [])].sort((a, b) => tierRank(a) - tierRank(b));
};

export const enabledSvodFilterInteractiveTool = (embeddedSvods: ISBEmbeddedSvods[], isSatellite: boolean) => {
  embeddedSvods?.forEach?.(svod => {
    svod.packageListSystem = orderPackageListSystem(svod?.packageListSystem, isSatellite);
  })

  return embeddedSvods?.filter?.((svod: ISBEmbeddedSvods) => svod?.interactiveToolData?.disable === false)?.filter?.(svod => (svod?.packageListSystem?.length > 0 || svod?.genrePacks?.length > 0)) || [];
}

// Generated by Copilot- Start
const ensureArray = <T>(value?: T[]): T[] => (Array.isArray(value) ? value : []);

const includesPackageId = (collection: string[] | undefined, packageId: string): boolean => (packageId ? ensureArray(collection).includes(packageId) : false);

export const singlePackageFilterInteractiveTool = (args: {
  embeddedSvods: ISBEmbeddedSvods[];
  singlePackageView?: boolean;
  singlePackageConfig?: any;
}) => {
  const { embeddedSvods, singlePackageView, singlePackageConfig } = args;
  const normalizedSvods = ensureArray(embeddedSvods);
  const { packageId = "", type } = singlePackageConfig || {};

  if (!singlePackageView || !packageId) {
    return normalizedSvods;
  }

  const matcher =
    type === "genre" ?
      (svod: ISBEmbeddedSvods) => includesPackageId(svod?.genrePacks, packageId) :
      (svod: ISBEmbeddedSvods) => includesPackageId(svod?.packageListSystem, packageId);

  return normalizedSvods.filter(matcher);
};

export const embeddedSVODParserInteractiveTool = (
  embeddedSvods: ISBEmbeddedSvods[],
  svodNameMapping?: SvodNameMapping
) => {
  const normalizedSvods = ensureArray(embeddedSvods);

  return normalizedSvods.map((svod: ISBEmbeddedSvods) => {
    const {
      interactiveToolData = {},
      offerCode = "",
      packageListSystem,
      genrePacks
    } = svod || ({} as ISBEmbeddedSvods);

    return {
      // WAT-11085: the interactive tool renders svod logos of ALL product types (genre/DTVI/BYOD/DTVS).
      // Resolve the name via the by-entry helper (displayName -> EPOCH svodNameMapping[code] ->
      // embedded name), mirroring in-page chlup: genre svods pick up their EPOCH name, while
      // DTVI/BYOD/DTVS svods (absent from svodNameMapping) keep svod.name — already the EPOCH offer
      // name set above by addPackageDetailsToEmbeddedSvods — so no product type is dropped. When no
      // svodNameMapping is supplied it collapses to displayName || name (the prior behavior).
      name: resolveGenreSvodNameByEntry(svod, {
        svodNameMapping,
        code: offerCode
      }),
      offerCode,
      interactiveToolData,
      packageListSystem: ensureArray(packageListSystem),
      genrePacks: ensureArray(genrePacks),
      // WAT-11710: this parser rebuilds a fresh object, so the entry's top-level productGroup
      // would otherwise be dropped. Carry it explicitly — it is the grouping key
      // collapseSameProductSvods reads at the interactive-tool adapter's tile-build step.
      productGroup: svod?.productGroup
    };
  });
};
// Generated by Copilot- End

// WAT-11085: canonical genre-SVOD display-name resolvers.
// These NEVER read the shop PCP-proxy feed name (svodGenreData[].name). The two shop-page
// components (channel-pack-lineup, package-compare-tool) intentionally keep using svodGenreData
// and must NOT be switched to these helpers.
// NOTE: the two resolvers below deliberately use DIFFERENT priorities — see each doc block.
// Do not "unify" them: …ByEntry honours the split displayName (Disney+/Hulu render individually),
// …ByCode intentionally ignores it so a shared bundle code renders as one EPOCH name.
type SvodNameMapping = Record<string, string> | null | undefined;

// A svod entry with the server-resolved display name attached (see attachResolvedSvodNames).
export type ResolvedSvod = ISBEmbeddedSvods & { channelNameLabel: string };

/**
 * Bundled name for TEXT-only genre surfaces (channel-lineup "(with …)" label,
 * marketing-banner [svod_names]). Resolved by EPOCH/offer code, so a shared bundle
 * code (e.g. Disney+, Hulu Bundle) renders as its single EPOCH name rather than a
 * split entry. Split displayName is deliberately ignored here.
 *
 * Priority: EPOCH svodNameMapping[code] -> embedded bundle name -> caller `fallbackName` -> "".
 * The raw offer code is NEVER returned — a customer-facing "(with …)" label must not leak a
 * BOLT-… SKU on an EPOCH gap. Text callers that have a human-readable shop name (channel-lineup,
 * interactive-tool categories) pass it as `fallbackName`; callers without one (marketing-banner)
 * omit it and the unresolved svod is dropped by their `.filter(Boolean)`.
 */
export const resolveGenreSvodNameByCode = (
  code: string | undefined,
  args: { svodNameMapping?: SvodNameMapping; embeddedSvods?: ISBEmbeddedSvods[]; fallbackName?: string }
): string => {
  const { svodNameMapping, embeddedSvods, fallbackName } = args || {};
  const epochName = code ? svodNameMapping?.[code] : undefined;
  if (epochName) {
    return epochName;
  }
  // Fallback: embedded name of the bundle entry (the one WITHOUT a split displayName),
  // else any embedded entry sharing that code, else the caller-supplied human-readable name.
  const matches = ensureArray(embeddedSvods).filter(
    svod => svod?.offerCode === code || svod?.offerCodeSatellite === code
  );
  const bundleEntry = matches.find(svod => !svod?.displayName) || matches[0];
  const resolved = bundleEntry?.name || fallbackName || "";
  if (!resolved) {
    // Nothing resolved anywhere (missing from EPOCH svodNameMapping AND embedded feed AND no
    // fallback) — surface it so a missing genre mapping is visible instead of a silent blank.
    logError("resolveGenreSvodNameByCode", new Error("Unresolved genre SVOD name"), {
      step: "resolveGenreSvodNameByCode",
      code: code || ""
    });
  }
  return resolved;
};

/**
 * Split name for LOGO-bearing genre surfaces (DtvMarquee, package-carousel, flip-cards).
 * displayName wins so split-intent svods (Disney+/Hulu) render individually and each
 * logo's alt/tooltip inherits the split name.
 */
export const resolveGenreSvodNameByEntry = (
  embeddedSvod: ISBEmbeddedSvods | undefined,
  args: { svodNameMapping?: SvodNameMapping; code?: string }
): string => {
  const { svodNameMapping, code } = args || {};
  const lookupCode = code ?? embeddedSvod?.offerCode;
  return (
    embeddedSvod?.displayName ||
    (lookupCode ? svodNameMapping?.[lookupCode] : undefined) ||
    embeddedSvod?.name ||
    ""
  );
};

/**
 * Flatten the per-package EPOCH included-svods feed (dtviEmbeddedSvodOffers, passed to the
 * adapters as `embeddedIncludedSvods`) into a single `offerCode -> EPOCH offer name` map.
 * A svod's offer name is the same regardless of which package included it, so a flat map is
 * equivalent to (and more robust than) a per-selected-package lookup. First non-empty name wins.
 */
export const buildIncludedSvodNameMap = (
  embeddedIncludedSvods?: Record<string, Array<{ code?: string; name?: string }>>
): Record<string, string> => {
  const map: Record<string, string> = {};
  Object.values(embeddedIncludedSvods || {}).forEach(list => {
    ensureArray(list as any[]).forEach(svod => {
      if (svod?.code && svod?.name && !map[svod.code]) {
        map[svod.code] = svod.name;
      }
    });
  });
  return map;
};

/**
 * WAT-11085: resolve the final display name for EVERY svod rendered by the two mixed-product
 * comparison tools (dtv-agent-package-compare-tool, custom-acq-compare-tool) and attach it as
 * `channelNameLabel`, so the client renders a finished string (no name logic client-side, and no
 * need to import this server-only resolver into the client bundle).
 *
 * These tools show genre packs AND DTVI/BYOD packages, which have DIFFERENT name sources:
 *   - genre svods  -> resolveGenreSvodNameByEntry: displayName -> EPOCH svodNameMapping -> embedded name
 *   - DTVI/BYOD svods -> their own EPOCH offer name (embeddedIncludedSvods) -> embedded name
 * A svod is treated as genre when it has a split displayName or a genre svodNameMapping entry;
 * otherwise its DTVI/BYOD EPOCH name is preserved (e.g. STARZ, HBO Max Standard, Paramount+ with
 * SHOWTIME — DTVI PREMIER includes that are NOT in the genre svodNameMapping).
 * Returns a NEW array of ResolvedSvod (channelNameLabel guaranteed); input entries are not mutated.
 * Idempotent: re-running on an already-resolved array recomputes the same channelNameLabel.
 */
export const attachResolvedSvodNames = (
  embeddedSvods: ISBEmbeddedSvods[] | undefined,
  svodNameMapping: SvodNameMapping,
  embeddedIncludedSvods?: Record<string, Array<{ code?: string; name?: string }>>
): ResolvedSvod[] => {
  const includedSvodNameMap = buildIncludedSvodNameMap(embeddedIncludedSvods);
  return ensureArray(embeddedSvods).map(svod => {
    const code = svod?.offerCode;
    const isGenreSvod = !!svod?.displayName || !!(code && svodNameMapping?.[code]);
    const channelNameLabel = isGenreSvod ?
      resolveGenreSvodNameByEntry(svod, {
        svodNameMapping,
        code
      }) :
      (code && includedSvodNameMap[code]) || svod?.name || "";
    return {
      ...svod,
      channelNameLabel
    };
  });
};
